import React from "react";
import injectSheet from "react-jss";
import {
	FormInputContainer,
	Image,
	InfoButton,
	NumberField,
	TextButton,
} from "../../";
import Arrow from "../../icons/arrowIcon";

class BudgetCalculator extends React.Component {
	constructor() {
		super();

		this.state = { open: false };

		this.getEquity = this.getEquity.bind(this);
		this.getMortgage = this.getMortgage.bind(this);
		this.getTotal = this.getTotal.bind(this);
		this.toggleOpen = this.toggleOpen.bind(this);
		this.handleChange = this.handleChange.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
	}

	componentDidMount() {
		const { inputs } = this.props.calculation;

		for (const input of inputs) {
			this.setState({
				[input]: "",
			});
		}
	}

	getEquity() {
		const { inputs } = this.props.calculation;

		return inputs.reduce(
			(accumulator, input) =>
				accumulator + (Number.parseInt(this.state[input], 10) || 0),
			0,
		);
	}

	getMortgage() {
		const { mortgage } = this.props.calculation;
		const equity = this.getEquity();
		return Number.parseInt((equity / (1 - mortgage)) * mortgage, 10);
	}

	getTotal() {
		return (
			Number.parseInt(this.getEquity(), 10) +
			Number.parseInt(this.getMortgage(), 10)
		);
	}

	toggleOpen() {
		this.setState({
			open: !this.state.open,
		});
	}

	handleChange(values, input) {
		const value = !Number.isNaN(values.floatValue) ? values.floatValue : "";

		this.setState({
			[input.target.name]: value,
		});
		this.setState({
			equity: this.getEquity(),
		});
	}

	handleSubmit() {
		const { config, min, setBudget } = this.props;

		const value = Math.min(
			Number.parseInt(Math.max(this.getTotal(), min) / config.increment, 10) *
				config.increment,
			config.maxBudget,
		);
		setBudget(value);
		this.setState({ open: false });
	}

	render() {
		const {
			calculation: { description, inputs, mortgage },
			config: { increment, currency },
			classes,
		} = this.props;

		const { open } = this.state;

		return (
			<div className={`${classes.budgetTileContainer} ${open && "open"}`}>
				<div
					className={`${classes.toggle}`}
					role="button"
					tabIndex={0}
					onClick={this.toggleOpen}
					onKeyDown={() => false}
				>
					<div>
						<div className={`${classes.titleBar}`}>
							<div>
								<Image src="images/static/calculator.svg" />
							</div>
							{description}
						</div>
						<div className={`${classes.toggleArrow}`}>
							<Arrow />
						</div>
					</div>
				</div>
				<div className={`${classes.elementContainer}`}>
					<div className={`${classes.calculationContainer}`}>
						<div
							className={`${classes.growContainer}`}
							style={{
								height: `${
									open
										? this.calculation && this.calculation.clientHeight + 15
										: 0
								}px`,
							}}
						>
							<div
								ref={(node) => {
									this.calculation = node;
								}}
							>
								<div className={`${classes.inputs}`}>
									{inputs.map((input) => (
										<FormInputContainer
											className={`${classes.inputContainer}`}
											filled={this.state[input] > 0}
											forHtml={input}
											key={`input-${input}`}
											label={input}
											right
										>
											<NumberField
												displayType="input"
												id={input}
												name={input}
												value={this.state[input]}
												handleChange={this.handleChange}
												handleSubmit={() => false}
												step={increment}
												suffix={` ${currency}`}
											/>
										</FormInputContainer>
									))}
								</div>
								<div className={`${classes.calcs}`}>
									<div className={`${classes.inputContainer}`}>
										<div className={`${classes.inputTitel}`}>
											<span>Eigenkapital</span>
											<span className={`${classes.remark}`}>
												({Math.round((1 - mortgage) * 100)}%)
											</span>
											<InfoButton
												modalText="Als Eigenkapital oder Eigenmittel gelten unter anderem Spar-/ Kontoguthaben, Wertschriften, Vorsorgegelder der 2. und 3. Säule, Lebensversicherungen (Rückkaufwert), Erbvorbezug, Schenkungen oder unverzinsliche und nicht rückzahlungspflichtige Darlehen. Die eigenen Mittel sollten 20% des Marktwertes ausmachen, wovon mindestens 10% aus Guthaben ausserhalb der 2. Säule stammen müssen."
												modalTitle="Eigenkapital"
												tip="info"
											/>
										</div>
										<div className={`${classes.inputSpan}`}>
											<NumberField
												displayType="text"
												name="equity"
												value={this.getEquity()}
												handleChange={() => false}
												handleSubmit={() => false}
												placeholder="Eigenkapital"
												suffix={` ${currency}`}
											/>
										</div>
									</div>
									<div className={`${classes.inputContainer}`}>
										<div className={`${classes.inputTitel}`}>
											<span>Fremdkapital</span>
											<span className={`${classes.remark}`}>
												({Math.round(mortgage * 100)}%)
											</span>
											<InfoButton
												modalText="Das Fremdkapital bezeichnet die Belehnung der Bank im Verhältnis zum Marktwert der Liegenschaft. Mindestens 20% des Kaufpreises müssen Sie mit Eigenmitteln finanzieren. Die Differenz dazu besteht aus maximal 80% Fremdkapital in Form eines Hypothekarkredites."
												modalTitle="Fremdkapital"
												tip="info"
											/>
										</div>
										<div className={`${classes.inputSpan}`}>
											<NumberField
												displayType="text"
												name="mortgage"
												value={this.getMortgage()}
												handleChange={() => false}
												handleSubmit={() => false}
												suffix={` ${currency}`}
											/>
										</div>
									</div>
								</div>
								<div className={`${classes.calcs}`}>
									<div className={`${classes.inputContainer}`}>
										<div className={`${classes.inputTitel}`}>
											<span>Total</span>
											<span className={`${classes.remark}`}>
												In Abhängigkeit von der Tragbarkeit
											</span>
											<InfoButton
												modalText="Die Tragbarkeit bezeichnet das Total folgender Aufwendungen im Verhältnis zu Ihrem Nettoeinkommen: Hypothekarzinsen auf Basis eines langfristigen Durchschnittzinssatzes von 5%, Amortisationen, Unterhalts- und Nebenkosten. Damit die Belastung für Sie nicht zu gross wird, sollten die so berechneten Aufwendungen maximal 35% des Nettolohnes betragen."
												modalTitle="Tragbarkeit"
												tip="info"
											/>
										</div>
										<div className={`${classes.inputSpan}`}>
											<NumberField
												displayType="text"
												name="total"
												value={this.getTotal()}
												handleChange={() => false}
												handleSubmit={() => false}
												suffix={` ${currency}`}
											/>
										</div>
									</div>
								</div>
								<div className={`${classes.submit}`}>
									<TextButton
										disabled={!(this.getTotal() > 0)}
										handleClick={this.handleSubmit}
										index={0}
										text="einstellen"
										primary
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}
}

const styles = (theme) => ({
	budgetTileContainer: {
		margin: `0px ${theme.sizes.gutter / 2}px`,
		backgroundColor: theme.colors.main_2,
		height: "100%",
		display: "flex",
		flexWrap: "wrap",
		justifyContent: "center",
		marginTop: `${theme.sizes.gutter}px`,
		position: "relative",
		transition: [
			{
				property: "padding",
				duration: theme.speed.medium,
			},
		],
		"&.open": {
			padding: "40px 0",
		},
		"&.open $toggleArrow": {
			transform: "rotate(270deg)",
		},
		"&.open $growContainer": {
			opacity: 1,
			transition: [
				{
					property: "height",
					duration: theme.speed.medium,
				},
				{
					property: "opacity",
					duration: theme.speed.fast,
					delay: theme.speed.fast,
				},
			],
		},
	},
	titleBar: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		paddingRight: `${theme.sizes.gutter}px`,
		[theme.media.medium]: {
			paddingRight: 0,
		},
		"& > div": {
			paddingRight: `${theme.sizes.gutter / 4}px`,
			height: "25px",
		},
	},
	elementContainer: {
		width: "100%",
		maxWidth: `${theme.sizes.smallContainer}px`,
	},
	calculationContainer: {
		width: "100%",
		padding: `0 ${theme.sizes.gutter / 2}px`,
		color: theme.colors.main_3,
		transition: [
			{
				property: "padding",
				duration: theme.speed.medium,
			},
		],
	},
	toggle: {
		width: "100%",
		textAlign: "center",
		color: theme.colors.main_3,
		cursor: "pointer",
		padding: `${theme.sizes.gutter / 2}px`,
		"& > div": {
			position: "relative",
		},
	},
	toggleArrow: {
		position: "absolute",
		top: 0,
		right: 0,
		width: "30px",
		height: "30px",
		cursor: "pointer",
		transform: "rotate(90deg)",
		transition: [
			{
				property: "transform",
				duration: theme.speed.medium,
			},
		],
		"& svg": {
			fill: theme.colors.main_3,
		},
	},
	growContainer: {
		overflow: "hidden",
		height: 0,
		opacity: 0,
		transition: [
			{
				property: "height",
				duration: theme.speed.medium,
				delay: theme.speed.fast,
			},
			{
				property: "opacity",
				duration: theme.speed.fast,
				delay: 0,
			},
		],
	},
	inputContainer: {
		padding: `${theme.sizes.gutter / 4}px 0`,
	},
	inputs: {},
	calcs: {
		borderTop: `2px solid ${theme.colors.main_3}`,
		marginTop: `${theme.sizes.gutter / 2}px`,
		"& $inputContainer": {
			display: "flex",
			flexWrap: "noWrap",
			fontSize: "60%",
			[theme.media.medium]: {
				fontSize: "100%",
			},
		},
	},
	inputSpan: {
		maxWidth: "160px",
		width: "100%",
		"& span": {
			padding: `${theme.sizes.gutter / 4}px ${theme.sizes.gutter / 2}px`,
			color: theme.colors.main_3,
			border: "none",
		},
	},
	inputTitel: {
		padding: `0 ${theme.sizes.gutter / 4}px`,
		display: "flex",
		alignItems: "center",
		width: "100%",
		"& > div": {
			marginLeft: `${theme.sizes.gutter / 2}px`,
			[theme.media.medium]: {
				marginLeft: 0,
			},
		},
	},
	remark: {
		padding: `0 ${theme.sizes.gutter / 4}px`,
		fontSize: "60%",
		display: "none",
		[theme.media.medium]: {
			display: "block",
		},
	},
	submit: {
		display: "flex",
		justifyContent: "flex-end",
		fontSize: "60%",
		[theme.media.medium]: {
			fontSize: "100%",
		},
		"& > div": {
			flexGrow: 0,
		},
	},
});

export default injectSheet(styles)(BudgetCalculator);



// WEBPACK FOOTER //
// ./src/modules/budget/components/budgetCalculation.jsx